// Single source of truth for year/semester dropdown options across the app.
// Returns the appropriate year/semester options based on the selected stream so that
// MBA subjects and batches no longer get accidentally saved with UG labels like 'SY'.

// Canonical stream list — use this everywhere instead of hardcoding stream lists.
export const STREAMS = [
  { value: 'Interior Design', label: 'Interior Design' },
  { value: 'Fashion Design', label: 'Fashion Design' },
  { value: 'MBA Design Management', label: 'MBA Design Management' },
  { value: 'Skill Enhancement', label: 'Skill Enhancement' }
];

export const STREAM_VALUES = STREAMS.map(s => s.value);

// Canonical year labels used app-wide. Use these when adding new options anywhere.
export const UG_YEARS = [
  { value: 'FY', label: 'First Year (FY)' },
  { value: 'SY', label: 'Second Year (SY)' },
  { value: 'TY', label: 'Third Year (TY)' },
  { value: 'Fourth Year', label: 'Fourth Year' }
];

export const MBA_YEARS = [
  { value: 'Year 1', label: 'Year 1' },
  { value: 'Year 2', label: 'Year 2' }
];

export const PG_YEARS = [
  { value: 'PG First Year', label: 'PG First Year' },
  { value: 'PG Second Year', label: 'PG Second Year' }
];

// Year options for a given stream.
// Non-MBA streams keep both UG + PG options for backward compatibility
// (Fashion/Interior have PG variants).
export const getYearOptions = (stream) => {
  if (stream === 'MBA Design Management') return MBA_YEARS;
  if (stream === 'Skill Enhancement') return []; // flat list, no year required
  return [...UG_YEARS, ...PG_YEARS]; // Fashion/Interior have both UG and PG cohorts
};

// Semester options scoped to a stream + year. Returns a flat list.
export const getSemesterOptions = (stream, year) => {
  if (stream === 'MBA Design Management') {
    if (year === 'Year 1') return [{ value: 'PG Sem 1', label: 'PG Sem 1' }, { value: 'PG Sem 2', label: 'PG Sem 2' }];
    if (year === 'Year 2') return [{ value: 'PG Sem 3', label: 'PG Sem 3' }, { value: 'PG Sem 4', label: 'PG Sem 4' }];
    return [];
  }
  if (stream === 'Skill Enhancement') return [];
  // Non-MBA streams
  const ugMap = {
    'FY': ['Semester 1', 'Semester 2'],
    'SY': ['Semester 3', 'Semester 4'],
    'TY': ['Semester 5', 'Semester 6'],
    'Fourth Year': ['Semester 7', 'Semester 8']
  };
  const pgMap = {
    'PG First Year': ['PG Sem 1', 'PG Sem 2'],
    'PG Second Year': ['PG Sem 3', 'PG Sem 4']
  };
  const sems = ugMap[year] || pgMap[year] || [];
  return sems.map(s => ({ value: s, label: s }));
};

// Runtime normalizer for year labels (UG + PG, across all streams).
// Uses semester as the unambiguous signal so subjects/batches with mismatched
// year labels (e.g., subject saved as "4th Year" vs batch as "Fourth Year")
// still match each other.
//
// Canonical year labels:
//   UG (any stream): 'FY' / 'SY' / 'TY' / 'Fourth Year'
//   PG MBA:          'Year 1' / 'Year 2'
//   PG Fashion/Interior: 'PG First Year' / 'PG Second Year'
//
// Records with empty/missing/mixed semester pass through unchanged.
export const normalizePGYear = (year, stream, semester) => {
  if (!semester || stream === 'Skill Enhancement') return year;
  const sems = String(semester).split(',').map(s => s.trim()).filter(Boolean);
  if (sems.length === 0) return year;

  const UG_GROUPS = {
    'FY': new Set(['Semester 1', 'Semester 2']),
    'SY': new Set(['Semester 3', 'Semester 4']),
    'TY': new Set(['Semester 5', 'Semester 6']),
    'Fourth Year': new Set(['Semester 7', 'Semester 8'])
  };
  const PG_Y1 = new Set(['PG Sem 1', 'PG Sem 2']);
  const PG_Y2 = new Set(['PG Sem 3', 'PG Sem 4']);
  const ALL_PG = new Set([...PG_Y1, ...PG_Y2]);

  // PG branch
  if (sems.every(s => ALL_PG.has(s))) {
    const isY1 = sems.every(s => PG_Y1.has(s));
    const isY2 = sems.every(s => PG_Y2.has(s));
    if (!(isY1 || isY2)) return year;
    if (stream === 'MBA Design Management') return isY1 ? 'Year 1' : 'Year 2';
    return isY1 ? 'PG First Year' : 'PG Second Year';
  }
  // UG branch
  for (const [label, groupSems] of Object.entries(UG_GROUPS)) {
    if (sems.every(s => groupSems.has(s))) return label;
  }
  return year;
};

// Runtime normalizer used ONLY for MBA when matching legacy data.
// New records use canonical 'Year 1' / 'Year 2'.
export const normalizeMBAYear = (year) => {
  if (['Year 1', 'PG First Year', 'PG FY', 'FY'].includes(year)) return 'Year 1';
  if (['Year 2', 'PG Second Year', 'PG SY', 'SY'].includes(year)) return 'Year 2';
  return year;
};
